import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { PromoBannerDetailsDialogComponent } from '@box-core/components';
import {
  AddressesService,
  AuthenticationService,
  LoaderService,
  PromoBannersService,
  SentryService,
  DialogService,
  SEOService,
  UserService,
  CoreService,
  GeolocationService,
  CouponsService
} from '@box-core/services';
import { LandingChain, APIError, BusinessVertical, PromoBanner } from '@box-types';
import { finalize, map, catchError, switchMap, tap } from 'rxjs/operators';
import {
  sortPromoBanners,
  filterEnabledItems,
  normalizeWebAction,
  placeResultToAddress,
  decorateAddressWithDescription,
  isPlaceResultDeliveryCompliant
} from '@box/utils';
import { Observable, of } from 'rxjs';
import { Location } from '@angular/common';
import { PromoBannerDetailsDialogResponse } from '@box-core/components/promo-banner-details-dialog/promo-banner-details-dialog.types';
import { GlobalStateService } from '@box-core/services/global-state.service';

interface RouterOptions {
  commands: string[];
  extras?: NavigationExtras;
}

@Injectable()
export class LandingPageService {
  constructor(
    private router: Router,
    private dialog: MatDialog,
    private seoService: SEOService,
    private coreService: CoreService,
    private userService: UserService,
    private sentryService: SentryService,
    private dialogService: DialogService,
    private loaderService: LoaderService,
    private activatedRoute: ActivatedRoute,
    private addressesService: AddressesService,
    private promoBannersService: PromoBannersService,
    private authenticationService: AuthenticationService,
    private geolocationService: GeolocationService,
    private location: Location,
    private globalStateService: GlobalStateService
  ) {}

  public setMetaTags(): void {
    const title = 'online_delivery_you_always_win';
    const url = '/';
    const options = { title, url };
    this.seoService.setTags(options);
  }

  public handlePromoBanners(): Observable<PromoBannerDetailsDialogResponse> {
    return this.promoBannersService.fetchPromoBanners().pipe(
      tap((promoBanners) => this.initializePromoBanners(promoBanners)),
      switchMap(() => this.initiatePromoBannersSequence()),
      catchError((error: APIError) => {
        this.sentryService.captureException(error);
        return of(null);
      })
    );
  }

  public handleRedirectionSequence(): void {
    const chainKey = this.activatedRoute.snapshot.queryParams.chain as string;
    if (chainKey) {
      return this.navigateTo({
        commands: ['/chains/closest-shop/', chainKey]
      });
    }
    if (!this.authenticationService.isAuthenticated) return;
    this.navigateTo({ commands: ['/home'] });
  }

  private initializePromoBanners(promoBanners: PromoBanner[]): void {
    const userSegments = this.globalStateService.getUser()?.segments ?? [];
    const availableCoupons = this.globalStateService.getAvailableCoupons();
    const eligibleGroupings = this.globalStateService.getEligibleGroupings();
    const enabledBanners = filterEnabledItems(promoBanners, userSegments, availableCoupons, eligibleGroupings);
    const sortedBanners = sortPromoBanners(enabledBanners);
    this.globalStateService.setPromoBanners(sortedBanners);
  }

  private initiatePromoBannersSequence(): Observable<PromoBannerDetailsDialogResponse> {
    const slug = this.activatedRoute.snapshot.queryParams.promo as string;
    if (!slug) return of(null);
    return this.getPromoBanner(slug).pipe(
      switchMap((promoBanner) => {
        if (promoBanner) {
          return this.openPromoDialog(promoBanner);
        }
        return of(null);
      }),
      catchError((error: APIError) => {
        this.sentryService.captureException(error, {
          domain: 'Promo Banners',
          domainDetails: 'Landing Page Service - Get Promo Banner',
          severity: 'warning'
        });
        return of(null);
      })
    );
  }

  private getPromoBanner(slug: string): Observable<PromoBanner> {
    const promoBanners = this.globalStateService.getPromoBanners();
    const localPromoBanner = promoBanners.find((p) => p.slug === slug);
    if (localPromoBanner) return of(localPromoBanner);
    this.loaderService.setState(true);
    return this.promoBannersService.fetchPromoBanner(slug).pipe(finalize(() => this.loaderService.setState(false)));
  }

  private getPromoBannerRouterOptions(promoBanner: PromoBanner): RouterOptions {
    const webAction = promoBanner?.webAction;
    if (!webAction) return { commands: ['/discover'] };
    const navigationUrl = normalizeWebAction(webAction);
    const urlTree = this.router.parseUrl(navigationUrl);
    const segment = urlTree.root?.segments[0]?.path ?? '/discover';
    return { commands: [segment], extras: { queryParams: urlTree.queryParams } };
  }

  private openPromoDialog(promoBanner: PromoBanner): Observable<PromoBannerDetailsDialogResponse> {
    const dialogConfig: MatDialogConfig = {
      restoreFocus: false,
      autoFocus: false,
      panelClass: 'box-dialog',
      data: { promoBanner }
    };
    return this.dialog
      .open(PromoBannerDetailsDialogComponent, dialogConfig)
      .afterClosed()
      .pipe(
        map((data: PromoBannerDetailsDialogResponse) => {
          this.location.replaceState('/');
          if (!data?.triggerAction) return null;
          const routerOptions = this.getPromoBannerRouterOptions(promoBanner);
          this.navigateTo(routerOptions);
          return data;
        })
      );
  }

  public getFoodLandingChains(): LandingChain[] {
    return this.generateLandingChains('food');
  }

  public getGroceriesLandingChains(): LandingChain[] {
    return this.generateLandingChains('groceries');
  }

  private generateLandingChains(businessVertical: BusinessVertical): LandingChain[] {
    const chains = this.coreService.chains.getValue();
    const chainsWithAssets = chains.filter(
      (chain) => chain.businessVertical === businessVertical && chain.image && chain.backgroundImage
    );
    if (!chainsWithAssets?.length) return [];
    return chainsWithAssets
      .sort((a, b) => a.chainIndex - b.chainIndex)
      .map((chain) => ({
        _id: chain._id,
        logo: chain.image,
        backgroundImage: chain.backgroundImage,
        name: chain.name,
        key: chain.chainKey,
        slug: chain.slug
      }));
  }

  public navigateTo(routerOptions: RouterOptions): void {
    return void this.router.navigate(routerOptions.commands, routerOptions?.extras);
  }

  public initiatePlaceSequence(placeResult?: google.maps.places.PlaceResult): void {
    if (!isPlaceResultDeliveryCompliant(placeResult)) {
      this.dialogService.openErrorDialog({
        userTitle: 'cannot_find_address',
        userMessage: 'missing_elements_from_address'
      });
      this.globalStateService.clearAddress();
      return;
    }
    const address = placeResultToAddress(placeResult);
    const decoratedAddress = decorateAddressWithDescription(address);
    this.addressesService.setAndStoreAddress(decoratedAddress);
    this.navigateTo({ commands: ['/home'] });
  }

  public geolocateUser(): void {
    this.loaderService.setState(true);
    this.geolocationService
      .getUserPlace$()
      .pipe(finalize(() => this.loaderService.setState(false)))
      .subscribe((result) => this.initiatePlaceSequence(result));
  }
}
